import React from 'react';
import { Info, Layout, Target, ArrowUp, BookOpen, Square, Image, Edit, Award, Users, Book, RefreshCw, Share } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const LinkedInOptimizationChecklist = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">LinkedIn Optimization Checklist</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Profile Optimization Guide</h2>
              <p className="text-gray-700">
                Maximize your LinkedIn profile's visibility and appeal with this step-by-step checklist. 
                Follow these tips to stand out in a competitive job market and attract the right opportunities.
              </p>
            </div>
          </div>
        </div>

        {/* Profile Completeness */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">1. Complete All Profile Fields</h3>
              <p className="mb-4">Ensure your profile is 100% complete. Missing fields can lower your visibility in search results.</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Professional headline</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>About section</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Work experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Skills and endorsements</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Education</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Certifications and courses</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Headline Optimization */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Edit className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">2. Optimize Your Headline</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Use all 120 characters available</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Include your job title, industry keywords, and a unique value proposition</span>
                </li>
              </ul>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium text-gray-600 mb-2">Example Headline:</p>
                <p className="text-gray-600 italic">"Digital Marketing Strategist | SEO Expert | Driving Growth Through Data"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Keywords */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Target className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">3. Use Relevant Keywords</h3>
              <p className="mb-4">Research job descriptions in your industry and include keywords in:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Headline</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>About section</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Work experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Projects</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Profile Picture */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Image className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">4. Professional Profile Picture</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Use a high-quality, professional photo</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Dress appropriately for your industry</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Avoid casual or unclear images</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Consider using AI tools to enhance professional headshots</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Book className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">5. Compelling About Section</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Write in the first person</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Highlight your achievements, career goals, and unique skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>End with a call to action</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Work Experience */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">6. Detailed Work Experience</h3>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Use bullet points to describe roles and responsibilities</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Include quantifiable results where possible</span>
                </li>
              </ul>
              <div className="bg-white p-4 rounded-lg">
                <p className="font-medium text-gray-600 mb-2">Example Achievement:</p>
                <p className="text-gray-600 italic">"Increased website traffic by 30% within six months through targeted content strategy."</p>
              </div>
            </div>
          </div>
        </div>

        {/* Skills Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Award className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">7. List Relevant Skills</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Add at least 10 skills that align with your career goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Prioritize skills that are frequently listed in job descriptions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Endorsements Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Users className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">8. Get Endorsements and Recommendations</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Reach out to colleagues and supervisors to endorse your skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Request LinkedIn recommendations that highlight your strengths and achievements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* URL Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Layout className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">9. Customize Your URL</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Edit your LinkedIn URL to include your name or personal brand</span>
                </li>
                <div className="bg-white p-4 rounded-lg mt-2">
                  <p className="text-gray-600 italic">Example: "linkedin.com/in/janedoe"</p>
                </div>
              </ul>
            </div>
          </div>
        </div>

        {/* Industry Groups */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Users className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">10. Join Industry Groups</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Participate in LinkedIn groups relevant to your field</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Share insights and engage with posts to expand your network</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">11. Showcase Projects and Achievements</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Use the "Projects" section to highlight key accomplishments</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Include links to portfolios, presentations, or articles</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Profile Updates */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">12. Regularly Update Your Profile</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Keep your profile current with new roles, certifications, and skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Regular updates signal to recruiters that you're active</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Content Engagement */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Share className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="font-semibold mb-4">13. Engage with Content</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Post updates, share articles, and comment on posts</span>
                </li>
                <li className="flex items-start gap-3">
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                  <span>Demonstrate thought leadership by writing LinkedIn articles or posts in your area of expertise</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Closing Note */}
        <div className="bg-gray-100 p-6 rounded-lg">
          <p className="text-gray-700">
            By following this checklist, you'll ensure your LinkedIn profile is optimized to attract job opportunities 
            and network connections. Start implementing these steps today to get an advantage in your career!
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LinkedInOptimizationChecklist;